import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSuperAdminContextUser } from '../authStorage';
import { useLanguage } from '../i18n/language';
import {
  createSuperAdminShareholder,
  deactivateSuperAdminShareholder,
  fetchSuperAdminShareholderCompanies,
  fetchSuperAdminShareholders,
  updateSuperAdminShareholder,
} from '../superAdminShareholdersApi';
import './BranchesPage.css';
import './DecisionsPage.css';
import './Modal.css';
import './UsersPage.css';

const PAGE_SIZE = 10;

const emptyForm = () => ({
  companyId: '',
  name: '',
  ownershipPercentage: '',
  type: '',
  shareStockType: '',
  phone: '',
  email: '',
  userName: '',
  password: '',
  isActive: true,
});

export default function SuperAdminShareholdersPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = {
    title: isAr ? 'إدارة المساهمين' : 'Shareholders Management',
    subtitle: isAr ? 'إدارة المساهمين على مستوى جميع الشركات مع فلاتر متقدمة.' : 'Manage shareholders across all companies with advanced filters.',
    deniedSub: isAr ? 'هذه الصفحة متاحة للسوبر أدمن فقط' : 'This page is available for Super Admin only',
    add: isAr ? 'إضافة مساهم' : 'Add shareholder',
    edit: isAr ? 'تعديل' : 'Edit',
    deactivate: isAr ? 'تعطيل' : 'Deactivate',
    total: isAr ? 'الإجمالي' : 'Total',
    active: isAr ? 'نشط' : 'Active',
    inactive: isAr ? 'غير نشط' : 'Inactive',
    hasAccount: isAr ? 'لديه حساب' : 'Has account',
    noAccount: isAr ? 'بدون حساب' : 'No account',
    search: isAr ? 'بحث بالاسم، الشركة، النوع، السهم، البريد، الجوال...' : 'Search by name, company, type, stock type, email, phone...',
    allCompanies: isAr ? 'كل الشركات' : 'All companies',
    allStatuses: isAr ? 'كل الحالات' : 'All statuses',
    allAccounts: isAr ? 'كل أنواع الحسابات' : 'All account types',
    allTypes: isAr ? 'كل الأنواع' : 'All types',
    allShareStocks: isAr ? 'كل أنواع الأسهم' : 'All stock types',
    minOwnership: isAr ? 'أقل ملكية %' : 'Min ownership %',
    maxOwnership: isAr ? 'أعلى ملكية %' : 'Max ownership %',
    sortByName: isAr ? 'الاسم' : 'Name',
    sortByCompany: isAr ? 'الشركة' : 'Company',
    sortByType: isAr ? 'النوع' : 'Type',
    sortByStock: isAr ? 'نوع السهم' : 'Stock type',
    sortByOwnership: isAr ? 'نسبة الملكية' : 'Ownership',
    sortByStatus: isAr ? 'الحالة' : 'Status',
    sortByAccount: isAr ? 'الحساب' : 'Account',
    sortById: isAr ? 'الرقم' : 'ID',
    asc: isAr ? 'تصاعدي' : 'Ascending',
    desc: isAr ? 'تنازلي' : 'Descending',
    reset: isAr ? 'إعادة ضبط' : 'Reset',
    loading: isAr ? 'جارٍ تحميل المساهمين...' : 'Loading shareholders...',
    noResults: isAr ? 'لا يوجد نتائج.' : 'No results.',
    showing: isAr ? 'عرض' : 'Showing',
    from: isAr ? 'من' : 'of',
    colName: isAr ? 'الاسم الكامل' : 'Full name',
    colCompany: isAr ? 'الشركة' : 'Company',
    colOwnership: isAr ? 'الملكية' : 'Ownership',
    colStatus: isAr ? 'الحالة' : 'Status',
    modalAddTitle: isAr ? 'إضافة مساهم' : 'Add shareholder',
    modalEditTitle: isAr ? 'تعديل مساهم' : 'Edit shareholder',
    company: isAr ? 'الشركة' : 'Company',
    name: isAr ? 'الاسم' : 'Name',
    ownershipPercentage: isAr ? 'نسبة الملكية %' : 'Ownership %',
    type: isAr ? 'النوع' : 'Type',
    shareStockType: isAr ? 'نوع السهم' : 'Stock type',
    phone: isAr ? 'الجوال' : 'Phone',
    email: isAr ? 'البريد الإلكتروني' : 'Email',
    userName: isAr ? 'اسم المستخدم (اختياري)' : 'Username (optional)',
    password: isAr ? 'كلمة المرور' : 'Password',
    newPassword: isAr ? 'كلمة مرور جديدة (اختياري)' : 'New password (optional)',
    activeShareholder: isAr ? 'المساهم نشط' : 'Shareholder is active',
    save: isAr ? 'حفظ' : 'Save',
    saveChanges: isAr ? 'حفظ التغييرات' : 'Save changes',
    adding: isAr ? 'جارٍ الإضافة...' : 'Adding...',
    saving: isAr ? 'جارٍ الحفظ...' : 'Saving...',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    required: isAr ? 'الاسم والشركة والنوع ونوع السهم ونسبة الملكية مطلوبة' : 'Name, company, type, stock type and ownership are required',
    loadFailed: isAr ? 'تعذر تحميل البيانات' : 'Unable to load data',
    createFailed: isAr ? 'تعذر إضافة المساهم' : 'Unable to add shareholder',
    updateFailed: isAr ? 'تعذر تحديث المساهم' : 'Unable to update shareholder',
    deactivateFailed: isAr ? 'تعذر تعطيل المساهم' : 'Unable to deactivate shareholder',
    confirmDeactivate: isAr ? 'هل تريد تعطيل هذا المساهم؟' : 'Do you want to deactivate this shareholder?',
  };

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [shareStockTypes, setShareStockTypes] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [filters, setFilters] = useState({
    q: '',
    companyId: '',
    isActive: 'all',
    hasAccount: 'all',
    type: '',
    shareStockType: '',
    minOwnership: '',
    maxOwnership: '',
    sortBy: 'name',
    sortDir: 'asc',
  });

  const loadData = useCallback(async (page = 1, nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const [listRes, companyRes] = await Promise.all([
        fetchSuperAdminShareholders({ page, pageSize: PAGE_SIZE, ...nextFilters }),
        fetchSuperAdminShareholderCompanies(),
      ]);
      setRows(listRes.items);
      setTypes(listRes.types);
      setShareStockTypes(listRes.shareStockTypes);
      setCompanies(companyRes);
      setCurrentPage(listRes.page);
      setTotalRows(listRes.total);
      setTotalPages(listRes.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [filters, t.loadFailed]);

  useEffect(() => {
    if (!getAccessToken()) {
      window.location.assign('/login');
      return;
    }
    if (!isSuperAdminContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData(1, filters);
  }, [loadData, filters]);

  const stats = useMemo(() => ({
    active: rows.filter((r) => r.isActive).length,
    withAccount: rows.filter((r) => r.hasAccount).length,
  }), [rows]);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    loadData(1, next);
  };

  const resetFilters = () => {
    const next = { q: '', companyId: '', isActive: 'all', hasAccount: 'all', type: '', shareStockType: '', minOwnership: '', maxOwnership: '', sortBy: 'name', sortDir: 'asc' };
    setFilters(next);
    loadData(1, next);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      companyId: String(item.companyId),
      name: item.name,
      ownershipPercentage: String(item.ownershipPercentage ?? ''),
      type: item.type || '',
      shareStockType: item.shareStockType || '',
      phone: item.phone || '',
      email: item.email || '',
      userName: item.userName || '',
      password: '',
      isActive: item.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.companyId || !form.name.trim() || !form.type.trim() || !form.shareStockType.trim() || !form.ownershipPercentage) {
      setError(t.required);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        companyId: Number(form.companyId),
        name: form.name.trim(),
        ownershipPercentage: Number(form.ownershipPercentage),
        type: form.type.trim(),
        shareStockType: form.shareStockType.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        userName: form.userName.trim() || undefined,
        isActive: form.isActive,
      };

      if (editId) {
        await updateSuperAdminShareholder(editId, { ...payload, newPassword: form.password.trim() || undefined });
      } else {
        await createSuperAdminShareholder({ ...payload, password: form.password.trim() || undefined });
      }

      closeModal();
      await loadData(currentPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : (editId ? t.updateFailed : t.createFailed));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (item) => {
    if (togglingId) return;
    setTogglingId(item.id);
    try {
      await updateSuperAdminShareholder(item.id, {
        companyId: Number(item.companyId),
        name: item.name,
        ownershipPercentage: item.ownershipPercentage,
        type: item.type,
        shareStockType: item.shareStockType,
        phone: item.phone || undefined,
        email: item.email || undefined,
        userName: item.userName || undefined,
        isActive: !item.isActive,
      });
      setRows((prev) => prev.map((r) => r.id === item.id ? { ...r, isActive: !item.isActive } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : t.updateFailed);
    } finally {
      setTogglingId(null);
    }
  };

  const deactivate = async (item) => {
    if (!window.confirm(t.confirmDeactivate)) return;
    try {
      await deactivateSuperAdminShareholder(item.id);
      await loadData(currentPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.deactivateFailed);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadData(page, filters);
  };

  if (accessDenied) {
    return (
      <div className="page">
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t.title}</span>
            <span className="page__topbar-sub">{t.deniedSub}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page users-page">
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editId ? t.modalEditTitle : t.modalAddTitle}
        subtitle={t.subtitle}
        icon={<Icon name="money" size={18} color="#fff" />}
        size="lg"
        variant="drawer-left"
        headerActions={(
          <button type="submit" form="shareholder-superadmin-form" className="btn btn--primary" disabled={submitting}>
            {submitting ? (editId ? t.saving : t.adding) : (editId ? t.saveChanges : t.save)}
          </button>
        )}
        footer={<button type="button" className="btn btn--ghost" disabled={submitting} onClick={closeModal}>{t.cancel}</button>}
      >
        <form id="shareholder-superadmin-form" className="mform" onSubmit={submitForm}>
          {error && <div className="mform__error" style={{ padding: '10px 12px', background: '#ffebee', borderRadius: 10 }}>{error}</div>}
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.company} <span>*</span></label>
              <select className="mform__select" value={form.companyId} onChange={(e) => setForm((p) => ({ ...p, companyId: e.target.value }))}>
                <option value="">{isAr ? 'اختر الشركة' : 'Select company'}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.name} <span>*</span></label>
              <input className="mform__input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.ownershipPercentage} <span>*</span></label>
              <input className="mform__input" type="number" min="0.1" max="100" step="0.1" value={form.ownershipPercentage} onChange={(e) => setForm((p) => ({ ...p, ownershipPercentage: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.type} <span>*</span></label>
              <input className="mform__input" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.shareStockType} <span>*</span></label>
              <input className="mform__input" value={form.shareStockType} onChange={(e) => setForm((p) => ({ ...p, shareStockType: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.phone}</label>
              <input className="mform__input" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.email}</label>
              <input className="mform__input" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.userName}</label>
              <input className="mform__input" value={form.userName} onChange={(e) => setForm((p) => ({ ...p, userName: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{editId ? t.newPassword : t.password}</label>
              <input className="mform__input" type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="mform__field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                {t.activeShareholder}
              </label>
            </div>
          </div>
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="money" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t.title}
            </span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <Icon name="plus" size={14} color="#fff" />
              {t.add}
            </button>
          </div>
        </header>

        <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total"><Icon name="money" size={13} /> {t.total}: {totalRows}</div>
        <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t.active}: {stats.active}</div>
        <div className="page__stat-chip page__stat-chip--new"><Icon name="user" size={13} /> {t.hasAccount}: {stats.withAccount}</div>
        </div>

        <div className="page__filters" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div className="page__search" style={{ minWidth: 280, flex: '1 1 320px' }}>
          <Icon name="search" size={14} color="#90a4ae" />
          <input className="page__search-input" value={filters.q} onChange={(e) => updateFilter('q', e.target.value)} placeholder={t.search} />
        </div>
        <select className="page__filter-select" value={filters.companyId} onChange={(e) => updateFilter('companyId', e.target.value)}>
          <option value="">{t.allCompanies}</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="page__filter-select" value={filters.isActive} onChange={(e) => updateFilter('isActive', e.target.value)}>
          <option value="all">{t.allStatuses}</option>
          <option value="active">{t.active}</option>
          <option value="inactive">{t.inactive}</option>
        </select>
        <select className="page__filter-select" value={filters.hasAccount} onChange={(e) => updateFilter('hasAccount', e.target.value)}>
          <option value="all">{t.allAccounts}</option>
          <option value="yes">{t.hasAccount}</option>
          <option value="no">{t.noAccount}</option>
        </select>
        <select className="page__filter-select" value={filters.type} onChange={(e) => updateFilter('type', e.target.value)}>
          <option value="">{t.allTypes}</option>
          {types.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <select className="page__filter-select" value={filters.shareStockType} onChange={(e) => updateFilter('shareStockType', e.target.value)}>
          <option value="">{t.allShareStocks}</option>
          {shareStockTypes.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input className="page__filter-select" type="number" min="0.1" max="100" step="0.1" value={filters.minOwnership} onChange={(e) => updateFilter('minOwnership', e.target.value)} placeholder={t.minOwnership} />
        <input className="page__filter-select" type="number" min="0.1" max="100" step="0.1" value={filters.maxOwnership} onChange={(e) => updateFilter('maxOwnership', e.target.value)} placeholder={t.maxOwnership} />
        <select className="page__filter-select" value={filters.sortBy} onChange={(e) => updateFilter('sortBy', e.target.value)}>
          <option value="name">{t.sortByName}</option>
          <option value="company">{t.sortByCompany}</option>
          <option value="type">{t.sortByType}</option>
          <option value="stockType">{t.sortByStock}</option>
          <option value="ownership">{t.sortByOwnership}</option>
          <option value="status">{t.sortByStatus}</option>
          <option value="hasAccount">{t.sortByAccount}</option>
          <option value="id">{t.sortById}</option>
        </select>
        <select className="page__filter-select" value={filters.sortDir} onChange={(e) => updateFilter('sortDir', e.target.value)}>
          <option value="asc">{t.asc}</option>
          <option value="desc">{t.desc}</option>
        </select>
        <button type="button" className="btn btn--ghost" onClick={resetFilters}>{t.reset}</button>
        </div>

        {error && !modalOpen && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t.loading}</div>}

        {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>#</th>
                <th>{t.colName}</th>
                <th>{t.colCompany}</th>
                <th>{t.colOwnership}</th>
                <th>{t.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item, idx) => (
                <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(item)}>
                  <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td><strong>{item.name || '—'}</strong></td>
                  <td>{item.companyName || '—'}</td>
                  <td>{item.ownershipPercentage != null ? `${item.ownershipPercentage}%` : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        className={`sa-toggle${item.isActive ? ' sa-toggle--on' : ''}`}
                        disabled={togglingId === item.id}
                        onClick={(e) => { e.stopPropagation(); toggleActive(item); }}
                        title={item.isActive ? t.active : t.inactive}
                      >
                        <span className="sa-toggle__thumb" />
                      </button>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: item.isActive ? '#02c3a6' : '#90a4ae' }}>
                        {item.isActive ? t.active : t.inactive}
                      </span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ padding: '4px 10px', fontSize: '0.76rem' }}
                        onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                      >
                        <Icon name="edit" size={12} /> {t.edit}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="users-empty">{t.noResults}</div>}
          {totalPages > 1 && (
            <div className="pagination">
              <span>{t.showing} {Math.min(totalRows, (currentPage - 1) * PAGE_SIZE + 1)}-{Math.min(totalRows, currentPage * PAGE_SIZE)} {t.from} {totalRows}</span>
              <div className="pagination__pages">
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
                ))}
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
